// src/guides/GuideProfile.js
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const GuideProfile = () => {
    const { fetchWithAuth, user, loggedIn, loading: authLoading } = useAuth();
    const [guide, setGuide] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [profileImage, setProfileImage] = useState(null);
    const [uploading, setUploading] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL;
    const navigate = useNavigate();

    // Fetch guide details
    useEffect(() => {
        if (!authLoading && !loggedIn) {
            navigate("/login");
            return;
        }

        const fetchGuide = async () => {
            try {
                const res = await fetchWithAuth(
                    `${API_URL}/guide-details?email=${encodeURIComponent(user?.email)}`
                );
                if (!res.ok) throw new Error(`Failed with status ${res.status}`);
                const data = await res.json();
                setGuide(data.guide);
            } catch (err) {
                console.error("Guide profile error:", err);
                setError("Error loading guide profile");
            } finally {
                setLoading(false);
            }
        };

        if (loggedIn && user?.email) fetchGuide();
    }, [loggedIn, authLoading, fetchWithAuth, API_URL, navigate, user]);

    // Fetch existing profile image
    useEffect(() => {
        const fetchProfileImage = async () => {
            try {
                const res = await fetch(`${API_URL}/profile-image?userEmail=${encodeURIComponent(user?.email)}`);
                const data = await res.json();
                if (data.profileImageUrl) setProfileImage(data.profileImageUrl);
            } catch (err) {
                console.error("Failed to fetch profile image:", err);
            }
        };

        if (user?.email) fetchProfileImage();
    }, [user, API_URL]);

    // Handle profile image upload
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("profileImage", file);
        formData.append("userEmail", user.email);

        try {
            setUploading(true);
            const res = await fetch(`${API_URL}/profile-image`, {
                method: "POST",
                body: formData,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || "Failed to upload image");
            setProfileImage(data.profileImageUrl);
            toast.success("Profile image uploaded successfully");
        } catch (err) {
            console.error("Upload error:", err);
            toast.error(err.message || "Failed to upload profile image");
        } finally {
            setUploading(false);
        }
    };

    if (authLoading || loading) return <p>Loading profile...</p>;
    if (error) return <p>{error}</p>;

    return (
        <article className="guide-profile-dashboard">
            <h1>👋 {guide?.name || "Guide"}</h1>

            <div className="profile-image-section">
                <img
                    src={profileImage || "/default-profile.png"}
                    alt="Profile"
                    className="profile-image"
                    style={{ width: 150, height: 150, borderRadius: "50%" }}
                />
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    style={{ marginTop: "10px" }}
                />
            </div>

            <div className="profile-details">
                <p><strong>Email:</strong> {guide?.email}</p>
                <p><strong>Phone:</strong> {guide?.phone}</p>
                <p>
                    <strong>Languages:</strong>{" "}
                    {Array.isArray(guide?.languages)
                        ? guide.languages.join(", ")
                        : guide?.languages}
                </p>
                <p>
                    <strong>Base Fare:</strong> {guide?.baseFare} ({guide?.fareType})
                </p>
            </div>

            <section>
                <h3>Description</h3>
                <p>{guide?.description}</p>
            </section>
        </article>
    );
};

export default GuideProfile;
